import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const termsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms of Service | Dulair Private Family Office',
  url: `${import.meta.env.VITE_SITE_URL || 'https://dulair.com'}/terms-of-service`,
  description:
    'Terms of Service for Dulair Private Family Office — investor eligibility criteria, risk warnings, and conditions for using our wealth management services.',
  lastReviewed: '2025-01-01',
  inLanguage: 'en-GB',
  publisher: {
    '@type': 'Organization',
    name: 'Dulair Private Family Office',
    url: import.meta.env.VITE_SITE_URL || 'https://dulair.com',
  },
};

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigate back to home and scroll to footer
  const handleBackToHome = () => {
    navigate('/', { state: { scrollTo: 'footer' } });
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Terms of Service | Dulair Private Family Office"
        description="Terms of Service for Dulair Private Family Office — investor eligibility criteria, risk warnings, and conditions for using our wealth management services."
        canonical="/terms-of-service"
        schema={termsSchema}
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-navy to-gray py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="text-sm">Back to Home</span>
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/70 text-sm md:text-base"
          >
            Last updated: January 2025
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8"
        >
          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">1. Agreement to Terms</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              By accessing or using the Dulair Private Family Office website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.
            </p>
            <p className="text-text-light text-sm leading-relaxed">
              These terms constitute a legally binding agreement between you and Dulair Private Family Office, a company registered in England and Wales.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">2. Services Description</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              Dulair Private Family Office provides wealth management, investment advisory, and family office services to high-net-worth individuals and families. Our services include:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>Property investment opportunities and management</li>
              <li>Healthcare sector investments</li>
              <li>Strategic investment advisory</li>
              <li>Portfolio management and diversification</li>
              <li>Family wealth preservation strategies</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">3. Investor Eligibility</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              Certain investment opportunities presented on our website are only available to certified or sophisticated investors as defined by the Financial Conduct Authority (FCA). By self-certifying as an eligible investor, you confirm that you meet one of the following criteria:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li><strong className="text-navy">High Net Worth Individual:</strong> Annual income of £100,000+ or net assets of £250,000+ (excluding primary residence and pension)</li>
              <li><strong className="text-navy">Sophisticated Investor:</strong> Member of a business angel network, or have made multiple investments in unlisted companies</li>
              <li><strong className="text-navy">Self-Certified Sophisticated Investor:</strong> Sufficient knowledge and experience to understand the risks involved</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">4. Risk Warnings</h2>
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-5 mb-4">
              <p className="text-navy text-sm font-medium mb-2">
                <i className="ri-error-warning-line mr-2"></i>
                Important Risk Information
              </p>
              <p className="text-text-light text-sm leading-relaxed">
                Investments in property and alternative assets carry significant risks. The value of investments can go down as well as up, and you may not get back the amount originally invested. Past performance is not a reliable indicator of future results.
              </p>
            </div>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>Investments may be illiquid and difficult to sell</li>
              <li>Returns are not guaranteed and may vary significantly</li>
              <li>Tax treatment depends on individual circumstances and may change</li>
              <li>You should only invest money you can afford to lose</li>
              <li>We recommend seeking independent financial advice before investing</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">5. Website Use</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              When using our website, you agree to:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>Provide accurate and complete information when requested</li>
              <li>Not use the website for any unlawful purpose</li>
              <li>Not attempt to gain unauthorised access to our systems</li>
              <li>Not reproduce, distribute, or modify any content without permission</li>
              <li>Not use automated systems to access the website without consent</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">6. Intellectual Property</h2>
            <p className="text-text-light text-sm leading-relaxed">
              All content on this website, including text, graphics, logos, images, and software, is the property of Dulair Private Family Office or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not use, reproduce, or distribute any content without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">7. Confidentiality</h2>
            <p className="text-text-light text-sm leading-relaxed">
              Any investment information, financial data, or proprietary materials shared with you through our services are confidential. You agree not to disclose such information to third parties without our express written consent. This obligation survives the termination of any relationship with us.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">8. Limitation of Liability</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              To the fullest extent permitted by law:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>We provide the website and information &ldquo;as is&rdquo; without warranties of any kind</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the fees paid by you for our services</li>
              <li>We are not responsible for third-party content or links on our website</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">9. Indemnification</h2>
            <p className="text-text-light text-sm leading-relaxed">
              You agree to indemnify and hold harmless Dulair Private Family Office, its directors, officers, employees, and agents from any claims, damages, losses, or expenses arising from your use of our website or services, your violation of these terms, or your infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">10. Regulatory Information</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              Dulair Private Family Office operates in accordance with applicable UK financial regulations. Please note:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>We are not authorised or regulated by the Financial Conduct Authority for investment advice</li>
              <li>Information provided is for general purposes and does not constitute financial advice</li>
              <li>You should seek independent professional advice before making investment decisions</li>
              <li>We comply with anti-money laundering (AML) and know your customer (KYC) requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">11. Termination</h2>
            <p className="text-text-light text-sm leading-relaxed">
              We reserve the right to terminate or suspend your access to our website and services at any time, without notice, for any reason, including breach of these terms. Upon termination, your right to use the website ceases immediately, but provisions regarding intellectual property, confidentiality, and limitation of liability shall survive.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">12. Governing Law</h2>
            <p className="text-text-light text-sm leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">13. Changes to Terms</h2>
            <p className="text-text-light text-sm leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on this page. Your continued use of the website after any changes constitutes acceptance of the new terms. We encourage you to review these terms periodically.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">14. Contact Information</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              For questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-cream rounded-xl p-5 space-y-2">
              <p className="text-navy text-sm font-medium">Dulair Private Family Office</p>
              <p className="text-text-light text-sm">Kings Head House, 15 London End</p>
              <p className="text-text-light text-sm">Beaconsfield, Buckinghamshire, HP9 2HN</p>
              <p className="text-text-light text-sm">Email: enquiries@dulairpfo.com</p>
              <p className="text-text-light text-sm">Phone: +44 203 151 0000</p>
            </div>
          </section>
        </motion.div>

        {/* Back to Home Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-full hover:bg-gold hover:text-navy transition-all duration-300 cursor-pointer text-sm font-medium"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </button>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-navy py-6">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/50 text-xs">
            © 2025 Dulair Private Family Office. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
